import { useMemo, useState } from "react";
import {
  Button,
  Flex,
  message,
  Modal,
  PaginationProps,
  Typography,
} from "antd";
import useModal from "@/hooks/useModal";
import { User } from "@/types/user";
import {
  useDeleteUser,
  useGetUsers,
  useGetUsersOptions,
} from "./hooks/useUser";
import { getColumns } from "./utils/table";
import CreateModal from "./components/CreateModal";
import EditModal from "./components/EditModal";
import ChangePasswordModal from "./components/ChangePasswordModal";
import { useUpdateUser } from "./hooks/useUser";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import { SortCondition } from "@/components/table/hooks/useSort";
import Table, { FilterOptions } from "@/components/table";
import { DEFAULT_PAGINATION } from "@/constants/table";

const UserPage = () => {
  const [sort, setSort] = useState<SortCondition | undefined>();
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] = useState<PaginationProps>();

  const {
    isModalOpen: isCreateModalOpen,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
  } = useModal<User>();
  const {
    modalData: changePasswordModalData,
    isModalOpen: isChangePasswordModalOpen,
    openModal: openChangePasswordModal,
    closeModal: closeChangePasswordModal,
  } = useModal<User>();
  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<User>();

  const { mutate: deleteUser } = useDeleteUser();
  const { mutate: updateUser } = useUpdateUser();
  const { data, isFetching, refetch } = useGetUsers({
    params: {
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
      include_inactive: true,
    },
  });

  // Get filter options
  const { data: options } = useGetUsersOptions({
    params: {
      only: "username,email",
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      username: (options || []).reduce((acc, item) => {
        if (
          item.username &&
          !acc.some((option) => option.value === item?.username)
        ) {
          acc.push({
            text: item.username,
            value: item.username,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      email: (options || []).reduce((acc, item) => {
        if (item.email && !acc.some((option) => option.value === item?.email)) {
          acc.push({
            text: item.email,
            value: item.email,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      // TODO: Wait until backend ready for request a only param with many to many
      // roles: (options || []).reduce((acc, item) => {
      //   console.log(item);
      //   item.roles?.map((role) => {
      //     if (role.name && !acc.some((option) => option.value === role.name)) {
      //       acc.push({
      //         text: role.name,
      //         value: role.name,
      //       });
      //     }
      //   });
      //   return acc;
      // }, [] as FilterOptions[]),
    }),
    [options]
  );

  const handleDelete = (user: User) => {
    Modal.confirm({
      title: "Remove this user?",
      content: "This action cannot be undone.",
      okText: "Remove User",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        deleteUser(user.id, {
          onSuccess: () => {
            message.success("Successfully removed user");
            refetch();
          },
        });
      },
    });
  };

  const handleToggleActive = (user: User, checked: boolean) => {
    updateUser(
      { ...user, is_active: checked },
      {
        onSuccess: () => {
          const action = checked ? "activated" : "deactivated";
          message.success(`Successfully ${action} user`);
          refetch();
        },
      }
    );
  };

  return (
    <>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Users Management
      </Typography.Title>

      <Flex vertical={true} gap={16}>
        <Flex vertical={false} justify="flex-end" align="center" gap={16}>
          <Button type="primary" onClick={() => openCreateModal()}>
            New User
          </Button>
        </Flex>

        <Table
          columns={(props) =>
            getColumns({
              ...props,
              filterOptions,
              onEdit: openEditModal,
              onDelete: handleDelete,
              changePassword: openChangePasswordModal,
              onToggleActive: handleToggleActive,
            })
          }
          dataSource={data?.data || []}
          loading={isFetching}
          pagination={{
            defaultPagination: {
              ...DEFAULT_PAGINATION,
              showSizeChanger: true,
            },
            dataPagination: data?.pagination,
            onChange: setPagination,
          }}
          scroll={{
            x: "max-content",
          }}
          initialSort={sort}
          onSort={setSort}
          onFilter={setFilters}
        />
      </Flex>

      <CreateModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onRefetch={refetch}
      />

      <EditModal
        isOpen={isEditModalOpen}
        data={editModalData}
        onClose={closeEditModal}
        onRefetch={refetch}
      />

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        data={changePasswordModalData}
        onClose={closeChangePasswordModal}
        onRefetch={refetch}
      />
    </>
  );
};

export default UserPage;
